// components/omore/OmoreUserAbuse.tsx
// View users with repeated flags and manage block status

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
    fetchFlaggedUsers,
    fetchUserFlaggedMessages,
    setUserBlockStatus
} from '../../services/omoreInteractionService';
import { exportUserAbuseSummaryToCSV, downloadCSV } from '../../services/omoreExportService';
import { SeverityColors } from '../../types/OmoreInteractionTypes';
import type { UserAbuseSummary, FlaggedMessage } from '../../types/OmoreInteractionTypes';
import './OmoreUserAbuse.css';

const CATEGORY_LABELS: Record<string, string> = {
    self_harm: 'Self-Harm',
    violence: 'Violence',
    sexual: 'Sexual',
    harassment: 'Harassment',
    hate: 'Hate',
    inappropriate: 'Inappropriate',
    prompt_injection: 'Prompt Injection',
    other: 'Other',
};

export const OmoreUserAbuse: React.FC = () => {
    const navigate = useNavigate();
    const { currentUser, signOut } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [users, setUsers] = useState<UserAbuseSummary[]>([]);
    const [selectedUser, setSelectedUser] = useState<UserAbuseSummary | null>(null);
    const [userMessages, setUserMessages] = useState<FlaggedMessage[]>([]);
    const [modalLoading, setModalLoading] = useState(false);
    const [blockReason, setBlockReason] = useState('');
    const [actionLoading, setActionLoading] = useState(false);

    useEffect(() => {
        loadUsers();
    }, []);

    const loadUsers = async () => {
        try {
            setLoading(true);
            setError('');
            const data = await fetchFlaggedUsers();
            setUsers(data);
        } catch (err: any) {
            console.error('Error loading flagged users:', err);
            setError(err.message || 'Failed to load users');
        } finally {
            setLoading(false);
        }
    };

    const openUserDetail = async (user: UserAbuseSummary) => {
        setSelectedUser(user);
        setModalLoading(true);
        setBlockReason('');
        try {
            const messages = await fetchUserFlaggedMessages(user.appleID);
            setUserMessages(messages);
        } catch (err) {
            console.error('Error loading user messages:', err);
            setUserMessages([]);
        } finally {
            setModalLoading(false);
        }
    };

    const handleToggleBlock = async () => {
        if (!selectedUser || !currentUser?.email) return;
        try {
            setActionLoading(true);
            const newBlocked = !selectedUser.isBlocked;
            await setUserBlockStatus(selectedUser.appleID, newBlocked, currentUser.email, blockReason);
            // Optimistically update local state
            setSelectedUser(prev => prev ? { ...prev, isBlocked: newBlocked } : null);
            setUsers(prev => prev.map(u =>
                u.appleID === selectedUser.appleID ? { ...u, isBlocked: newBlocked } : u
            ));
            setBlockReason('');
            alert(newBlocked ? 'User blocked successfully.' : 'User unblocked successfully.');
        } catch (err: any) {
            console.error('Error toggling block:', err);
            alert('Failed to update block status.');
        } finally {
            setActionLoading(false);
        }
    };

    const handleSignOut = async () => {
        try {
            await signOut();
            navigate('/login');
        } catch (error) {
            console.error('Failed to sign out:', error);
        }
    };

    const formatTimestamp = (ts: any): string => {
        if (!ts) return 'N/A';
        try {
            const date = ts.toDate ? ts.toDate() : new Date(ts);
            return date.toLocaleDateString();
        } catch {
            return 'N/A';
        }
    };

    return (
        <div className="omore-abuse-container">
            <header className="omore-abuse-header">
                <div className="header-content">
                    <div>
                        <h1>User Flags</h1>
                        <p className="header-subtitle">Users with repeated moderation flags in Ask Omore</p>
                    </div>
                    <div className="header-actions">
                        <button onClick={() => navigate('/omore/moderation')} className="back-button">← Moderation Queue</button>
                        {users.length > 0 && (
                            <button
                                onClick={() => {
                                    const csv = exportUserAbuseSummaryToCSV(users);
                                    downloadCSV(csv, `omore-user-abuse-${Date.now()}.csv`);
                                }}
                                className="export-button"
                            >
                                Export CSV
                            </button>
                        )}
                        <span className="user-email">{currentUser?.email}</span>
                        <button onClick={handleSignOut} className="logout-button">Sign Out</button>
                    </div>
                </div>
            </header>

            <div className="omore-abuse-main">
                {loading && (
                    <div className="loading-state">
                        <div className="loading-spinner"></div>
                        <p>Loading flagged users...</p>
                    </div>
                )}

                {error && (
                    <div className="error-state">
                        <p>{error}</p>
                        <button onClick={loadUsers} className="retry-button">Retry</button>
                    </div>
                )}

                {!loading && !error && users.length === 0 && (
                    <div className="empty-state">
                        <div className="empty-icon">✓</div>
                        <h3>No flagged users</h3>
                        <p>No users have been flagged at this time.</p>
                    </div>
                )}

                {!loading && !error && users.length > 0 && (
                    <div className="user-table-wrapper">
                        <table className="user-table">
                            <thead>
                                <tr>
                                    <th>Explorer</th>
                                    <th>Total Flags</th>
                                    <th>Highest Severity</th>
                                    <th>Top Categories</th>
                                    <th>First Flagged</th>
                                    <th>Last Flagged</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map(user => {
                                    const topCats = Object.entries(user.flagsByCategory)
                                        .sort((a, b) => b[1] - a[1])
                                        .slice(0, 3);
                                    return (
                                        <tr key={user.appleID} className={user.isBlocked ? 'blocked-row' : ''}>
                                            <td className="user-name-cell">
                                                <div className="user-name">{user.explorerName}</div>
                                                <div className="user-id">{user.appleID.slice(0, 12)}...</div>
                                            </td>
                                            <td className="flags-count">{user.totalFlags}</td>
                                            <td>
                                                <span className="severity-badge" style={{
                                                    background: (SeverityColors[user.highestSeverity] || '#9ca3af') + '20',
                                                    color: SeverityColors[user.highestSeverity] || '#9ca3af',
                                                    border: `1px solid ${SeverityColors[user.highestSeverity] || '#9ca3af'}`,
                                                }}>
                                                    Severity {user.highestSeverity}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="cat-tags">
                                                    {topCats.map(([cat, count]) => (
                                                        <span key={cat} className="small-tag">{CATEGORY_LABELS[cat] || cat} ({count})</span>
                                                    ))}
                                                </div>
                                            </td>
                                            <td>{formatTimestamp(user.firstFlaggedAt)}</td>
                                            <td>{formatTimestamp(user.lastFlaggedAt)}</td>
                                            <td>
                                                {user.isBlocked
                                                    ? <span className="status-blocked">Blocked</span>
                                                    : <span className="status-active">Active</span>
                                                }
                                            </td>
                                            <td>
                                                <button onClick={() => openUserDetail(user)} className="action-button view-button">
                                                    View Details
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* User Detail Modal */}
            {selectedUser && (
                <div className="modal-overlay" onClick={() => setSelectedUser(null)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{selectedUser.explorerName} — Abuse Summary</h2>
                            <button onClick={() => setSelectedUser(null)} className="close-button">×</button>
                        </div>
                        <div className="modal-body">
                            {modalLoading ? (
                                <div className="loading-state" style={{ padding: '40px', color: '#2d3748' }}>
                                    <div className="loading-spinner" style={{ borderTopColor: '#667eea', borderColor: 'rgba(0,0,0,0.1)' }}></div>
                                    <p>Loading details...</p>
                                </div>
                            ) : (
                                <>
                                    <div className="user-summary-grid">
                                        <div><strong>Apple ID:</strong> {selectedUser.appleID}</div>
                                        <div><strong>Profile ID:</strong> {selectedUser.profileID}</div>
                                        <div><strong>Total Flags:</strong> {selectedUser.totalFlags}</div>
                                        <div><strong>Highest Severity:</strong> <span style={{ color: SeverityColors[selectedUser.highestSeverity] }}>{selectedUser.highestSeverity}</span></div>
                                        <div><strong>First Flagged:</strong> {formatTimestamp(selectedUser.firstFlaggedAt)}</div>
                                        <div><strong>Last Flagged:</strong> {formatTimestamp(selectedUser.lastFlaggedAt)}</div>
                                        <div><strong>Status:</strong> {selectedUser.isBlocked ? <span className="status-blocked">Blocked</span> : <span className="status-active">Active</span>}</div>
                                    </div>

                                    {/* Flag category breakdown */}
                                    <div className="category-breakdown">
                                        <h4>Flag Categories</h4>
                                        {Object.entries(selectedUser.flagsByCategory)
                                            .sort((a, b) => b[1] - a[1])
                                            .map(([cat, count]) => (
                                                <div key={cat} className="cat-row">
                                                    <span className="cat-name">{CATEGORY_LABELS[cat] || cat}</span>
                                                    <span className="cat-count">{count}</span>
                                                </div>
                                            ))}
                                    </div>

                                    {/* Sample flagged messages */}
                                    <div className="sample-messages">
                                        <h4>Sample Flagged Messages</h4>
                                        {userMessages.length === 0 && <p className="no-messages">No sample messages available.</p>}
                                        {userMessages.map((msg, i) => (
                                            <div key={msg.id} className="sample-msg">
                                                <div className="sample-msg-header">
                                                    <span className="sample-msg-num">#{i + 1}</span>
                                                    <span className="sample-msg-date">{formatTimestamp(msg.flaggedAt)}</span>
                                                    <a
                                                        href={`/omore/sessions/${msg.sessionID}`}
                                                        className="sample-msg-session"
                                                        onClick={e => { e.preventDefault(); navigate(`/omore/sessions/${msg.sessionID}`); setSelectedUser(null); }}
                                                    >
                                                        View Session
                                                    </a>
                                                </div>
                                                <p className="sample-msg-content">{msg.messageContent}</p>
                                                <div className="sample-msg-flags">
                                                    {msg.flagCategories.map(cat => (
                                                        <span key={cat} className="small-tag">{CATEGORY_LABELS[cat] || cat}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Block/Unblock */}
                                    <div className="block-section">
                                        {!selectedUser.isBlocked && (
                                            <>
                                                <label>Block Reason</label>
                                                <textarea
                                                    value={blockReason}
                                                    onChange={e => setBlockReason(e.target.value)}
                                                    placeholder="Enter reason for blocking..."
                                                    rows={2}
                                                />
                                            </>
                                        )}
                                        <button
                                            className={`block-button ${selectedUser.isBlocked ? 'unblock' : 'block'}`}
                                            onClick={handleToggleBlock}
                                            disabled={actionLoading}
                                        >
                                            {selectedUser.isBlocked ? 'Unblock User' : 'Block User'}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
